/* eslint-disable @typescript-eslint/no-deprecated */
import {
  createContext,
  useContext,
  useLayoutEffect,
  type RefObject,
} from 'react';

import type { PagePanelSizes, PanelName } from './page-types.js';

interface BasePagePanelState {
  ref: RefObject<HTMLDivElement | null>;
  isControlled: boolean;
  isDismissed: boolean;
  minWidth: number;
  breakpoint: number;
}

interface MainPagePanelState extends BasePagePanelState {
  isControlled: false;
  isDismissed: false;
}

/** @internal */
export interface PageLayoutState {
  sidebar: BasePagePanelState | null;
  main: MainPagePanelState | null;
  details: BasePagePanelState | null;
}

/** @internal */
export interface PageContext {
  containerSize: number;
  panels: PageLayoutState;
  registerPanel: (
    name: PanelName,
    options: {
      ref: RefObject<HTMLDivElement | null>;
      isControlled: boolean;
      isDismissed: boolean;
      minWidth: number;
      breakpoint: number;
    },
  ) => void;
  removePanel: (name: PanelName) => void;
  dismissPanel: (name: PanelName) => void;
  expandPanel: (name: PanelName) => void;
  togglePanel: (name: PanelName) => void;
  getPanelSizes: () => PagePanelSizes;
}

/** @internal */
export const pageContext = createContext<PageContext>({
  containerSize: 0,
  panels: { sidebar: null, main: null, details: null },
  registerPanel: () => undefined,
  removePanel: () => undefined,
  dismissPanel: () => undefined,
  expandPanel: () => undefined,
  togglePanel: () => undefined,
  getPanelSizes: () => undefined as unknown as PagePanelSizes,
});

/** @internal */
export function usePageContext() {
  return useContext(pageContext);
}

interface Options {
  ref: RefObject<HTMLDivElement | null>;
  /**
   * Whether the dismissed state is controlled externally.
   * @defaultValue false
   */
  isControlled?: boolean;
  /**
   * Whether the panel is dismissed (collapsed) or not.
   * @defaultValue false
   */
  isDismissed?: boolean;
  /**
   * The minimum width (in px) the panel can be resized to.
   * @defaultValue 0
   */
  minWidth?: number;
  /**
   * The breakpoint (in px) at which the panel is displayed as a `Drawer`.
   * `0` means the panel will never be displayed as a `Drawer`.
   * @defaultValue 0
   */
  breakpoint?: number;
}

/** @internal */
export function useRegisterPagePanel(name: PanelName, options: Options) {
  const { registerPanel, removePanel, panels } = usePageContext();
  const panel = panels[name];

  useLayoutEffect(() => {
    registerPanel(name, {
      ref: options.ref,
      isControlled: options.isControlled ?? false,
      isDismissed: options.isDismissed ?? false,
      minWidth: options.minWidth ?? 0,
      breakpoint: options.breakpoint ?? 0,
    });

    return () => {
      removePanel(name);
    };
  }, [
    name,
    options.breakpoint,
    options.isControlled,
    options.isDismissed,
    options.minWidth,
    options.ref,
    registerPanel,
    removePanel,
  ]);

  return panel;
}
